import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ComponentProps } from "react";
import { PageHeaderSkeleton, TableCardSkeleton } from "@/components/loading";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { ResponsesTable } from "@/components/responses-table/responses-table";
import { z } from "zod";
import { trpcClient, useTRPC } from "@/lib/trpc";

type ResponsesCallbackParams = Parameters<ComponentProps<typeof ResponsesTable>["callbackFn"]>[0];

const responsesSearchSchema = z.object({
  promptId: z.string().optional(),
  skip: z.number().optional().default(0),
  limit: z.number().optional().default(25),
  sort: z.string().optional(),
});

export const Route = createFileRoute("/_dashboard/$brand/searches/$search/responses")({
  component: RouteComponent,
  validateSearch: responsesSearchSchema,
});

function RouteComponent() {
  const { brand, search } = useParams({ from: "/_dashboard/$brand/searches/$search/responses" });
  const { promptId } = Route.useSearch();
  const trpc = useTRPC();

  // Fetch brand by slug from URL params
  const { data: brandObject, isLoading: isLoadingBrand } = useQuery(
    trpc.brands.getBrandBySlug.queryOptions({ slug: brand }),
  );

  const responsesCallback = async ({
    skip,
    take,
    promptId,
    sortBy,
    sortOrder,
    textFilter,
  }: ResponsesCallbackParams) =>
    trpcClient.promptResponses.getSearchSpecificationPromptResponses.query({
      searchSpecificationId: search!,
      promptId,
      brandId: brandObject!.id,
      skip,
      take,
      sortBy,
      sortOrder,
      textFilter,
    });

  // Show loading while fetching brand
  if (isLoadingBrand || !brandObject?.id) {
    return (
      <DashboardPageShell contentClassName="space-y-6">
        <PageHeaderSkeleton compact />
        <TableCardSkeleton rows={7} columns={6} showToolbar showFooter />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell contentClassName="space-y-6">
      <DashboardPageHeader
        title="Prompt Responses"
        description="Explore model answers with brand scoring and source citations."
      />
      <ResponsesTable
        promptId={promptId || null}
        callbackFn={responsesCallback}
        brandId={brandObject.id}
        searchSpecificationId={search}
      />
    </DashboardPageShell>
  );
}
